import { useParams, Link } from 'react-router-dom';
import { categories, articles } from '@/mocks/data';
import PageSEO from '@/components/feature/PageSEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.category === slug);

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-3">分类未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm whitespace-nowrap cursor-pointer">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 年轻的朋友韩国`,
    description: category.description,
    url: `https://hallyuway.github.io/category/${category.slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: categoryArticles.map((a, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `https://hallyuway.github.io/article/${a.slug}`,
        name: a.title,
      })),
    },
  };

  return (
    <>
      <PageSEO
        title={`${category.name} - 韩国旅行攻略`}
        description={category.description}
        keywords={`${category.name},韩国旅行,年轻人旅游攻略,韩国自由行`}
        canonicalUrl={`/category/${category.slug}`}
        ogImage={category.image}
        schema={categorySchema}
      />
      <ScrollToTop />
      <Navbar />
      <main>
        <div className="pt-24 md:pt-28 pb-10 md:pb-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category.name },
              ]}
            />
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mt-4">
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                  {category.name}
                </h1>
                <p className="text-foreground-500 text-sm md:text-base max-w-lg">
                  {category.description}
                </p>
              </div>
              <span className="text-sm text-foreground-400 bg-background-50 px-3 py-1.5 rounded-full whitespace-nowrap self-start">
                共 {category.articleCount} 篇攻略
              </span>
            </div>
          </div>
        </div>

        <div className="px-4 md:px-6 py-6 bg-background-50 border-b border-background-200/60 sticky top-16 z-30">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-2 overflow-x-auto pb-1">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer flex-shrink-0 ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <section className="py-10 md:py-14 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            {categoryArticles.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-foreground-500">该分类暂无文章</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-300 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative h-44 md:h-48 overflow-hidden">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="px-2.5 py-1 bg-primary-500 text-background-50 text-xs font-medium rounded-full whitespace-nowrap">
                          {article.tags[0]}
                        </span>
                      </div>
                    </div>
                    <div className="p-5">
                      <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2 mb-4">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full overflow-hidden bg-background-200">
                            <img
                              src={article.authorAvatar}
                              alt={article.author}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          </div>
                          <span className="text-xs text-foreground-500">{article.author}</span>
                        </div>
                        <span className="text-xs text-foreground-400">{article.publishDate}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}